import { create } from 'zustand'
import { Track, type RemoteTrackPublication, type Room } from 'livekit-client'
import { sounds } from '../services/sounds.js'
import { getSocket } from '../services/socket.js'
import { mediaManager } from '../services/mediaManager.js'
import { useLivekitStore } from './livekitStore.js'
import { useDmCallStore } from '@os/messenger/web/stores/dmCallStore.js'
import { applyManualToggle, applyBackgroundChange } from './autoFaceCenterCoupling'
import { emitVoiceActivity } from '../services/voiceActivity'
// authStore imports useVoiceStore so this is a circular reference, but
// the cycle is safe: authStore only touches useVoiceStore.getState()
// inside its logout/etc. callbacks (post-init), and we only touch
// useAuthStore.getState() inside this store's actions for the same
// reason. Both stores are fully evaluated before any action fires.
import { useAuthStore } from './authStore'
import { getIncomingLkQuality, type IncomingQuality } from '../services/videoQuality'
import { readVideoSpeakersOnly } from '../services/videoFocus'
import type { VoiceState } from '../types'
import type { ActivityEvent } from '../services/historyTypes'

export interface TranscriptEntry {
  id: string
  // Stable per-speaker ID — used by the live-partial path to know which
  // partial to replace when more text streams in for the same speech
  // burst. Optional because legacy client-emitted transcripts (pre-
  // Deepgram) didn't include it; new entries from the server always do.
  speakerId?: string
  speakerName: string
  text: string
  // LLM-cleaned version of `text` produced asynchronously by the
  // `transcript-cleanup` worker job (gpt-4o-mini). Null until the job
  // completes — UI renders `cleanedText ?? text`.
  cleanedText?: string | null
  timestamp: number
  // Wall-clock time of the LAST Deepgram final inside this burst.
  // Optional for legacy entries; the panel falls back to `timestamp`
  // plus a length-based estimate when it's missing.
  endTimestamp?: number
}

// In-progress utterance from a single speaker. The server emits these
// while Deepgram is still streaming finals for the same speech burst;
// once the speaker pauses for ~1.5s the server flushes the burst as a
// committed `transcript:new` and we drop the partial for that speaker.
export interface LivePartial {
  id: string
  speakerId: string
  speakerName: string
  text: string
  timestamp: number
  // Wall-clock time of the most recent Deepgram final the server has
  // appended into this partial. Updates with each `transcript:partial`
  // emit, so the UI can do "gap-from-last-word" math on the live
  // bubble too (not just on committed ones).
  endTimestamp?: number
}

// One floating emoji burst. Lives ~2.5s in the store, then is reaped by
// a setTimeout — long enough for the rise/fade animation to finish but
// short enough that pile-ups don't memory-leak the tile.
export interface LiveReaction {
  id: string
  emoji: string
  ts: number
}

// Per-burst lifetime — must stay in sync with the
// `.animate-reaction-float` keyframe in css/animations.css.
const REACTION_LIFETIME_MS = 2500
// Rate limit so a user holding the emoji button can't flood the room.
// Discord uses ~500ms here; 600ms feels comfortable while still letting
// the user fire a quick "👏👏👏" volley.
const REACTION_SEND_COOLDOWN_MS = 600
// Quick-pick palette shown in the controls popover. Kept short on purpose
// — Discord's research shows users overwhelmingly stick to <10 reactions
// in calls, and a long grid distracts from the meeting.
export const QUICK_REACTIONS = ['👍', '👏', '😂', '❤️', '🎉'] as const

interface VoiceStoreState {
  currentChannelId: string | null
  // OSPROD-104: one-shot intent — the voice channel the user explicitly
  // clicked to join. Set by ChannelList, consumed once by VoiceChannelView
  // on mount. Ephemeral (NOT persisted) so a refresh / deep-link never
  // auto-joins.
  pendingAutoJoinChannelId: string | null
  // One-shot mic policy for the pending auto-join. 'unmute' = phone-admit
  // join (admit = answer the phone, mic comes up live). Cleared together
  // with pendingAutoJoinChannelId.
  pendingAutoJoinMicPolicy: 'unmute' | null
  currentServerId: string | null
  joinedAt: number | null
  isVideoOn: boolean
  isScreenSharing: boolean
  isScreenSharingAudio: boolean
  participants: VoiceState[]
  // Channel ban the user just hit (join-time 403 or live voice:banned).
  // Drives <ChannelBanScreen>. Cleared on a fresh connect() / Close.
  bannedInfo: { channelId: string; reason: string | null; expiresAt: string | null } | null
  setBannedInfo: (info: { channelId: string; reason: string | null; expiresAt: string | null } | null) => void
  // Sticky voice ownership (2026-07-07): set when the server's voice:claim
  // pre-check refuses an auto-join because a DIFFERENT live session (the
  // desktop app, another browser) currently owns this channel's voice.
  // Drives the view-only "on another device" banner + "Move here" button.
  // Cleared on a successful connect() / explicit takeover. Mirrors the
  // bannedInfo pattern above.
  voiceActiveElsewhere: { channelId: string; deviceLabel: string } | null
  setVoiceActiveElsewhere: (v: { channelId: string; deviceLabel: string } | null) => void
  audioInputDeviceId: string | null
  audioOutputDeviceId: string | null
  // OSPROD-101: selected camera (videoinput) deviceId, mirrors the mic
  // picker. Persisted to localStorage; the VideoDeviceSelector switches
  // the live camera track via mediaManager.setVideoDevice.
  videoInputDeviceId: string | null
  // OSPROD-103: mirror the local self-view camera tile. Default on
  // (Zoom/Meet behaviour); toggled from the camera settings modal.
  selfMirror: boolean
  isNoiseSuppressionOn: boolean
  // Camera capture target. 'auto' uses the current default (1080p with
  // dynacast simulcast layers). 'low' / 'hd' / 'fhd' force a specific
  // resolution at the publisher — handy when the user is on a weak
  // upload link and wants to drop bitrate without LiveKit's adaptive
  // logic deciding for them. Persisted in localStorage so the choice
  // survives page reload and Electron restart.
  videoQuality: 'auto' | 'low' | 'hd' | 'fhd'
  // OSPROD-92: persistent record of the user's last camera ON/OFF flip.
  // Mirrors how `user.settings.isMuted` persists mic intent — without an
  // equivalent for the camera the reconnect / page-refresh path had no
  // way to know "camera was on before", so it always came back off.
  // Written by livekitStore.toggleVideo on every successful flip; read
  // by livekitStore.connect (after mic restore) and the VoiceManager
  // socket-reconnect handler, plus the mediaManager health check.
  // Persisted in localStorage so it survives full reloads.
  lastCameraEnabled: boolean
  // Receive-side cap applied to every remote video track via
  // RemoteTrackPublication.setVideoQuality. 'auto' = HIGH (let SFU pick
  // the best layer that fits our downlink). 'medium' / 'low' force a
  // ceiling for users whose downlink can't comfortably carry multiple
  // 1080p streams. Persisted in localStorage.
  incomingQuality: IncomingQuality
  /**
   * "Видео только говорящих" (2026-06-12 big-call request): when true,
   * VoiceManager unsubscribes (SFU-level) from the camera of everyone
   * who hasn't spoken recently — receive bandwidth/decode scales with
   * active speakers, not room size. Persisted in localStorage.
   */
  videoSpeakersOnly: boolean
  setVideoSpeakersOnly: (on: boolean) => void
  forceMutedChannels: Set<string>
  isForceMutedHere: (channelId: string | null | undefined) => boolean
  setForceMutedHere: (channelId: string, locked: boolean) => void
  /**
   * Apple-Center-Stage-style auto-framing on the local camera. When
   * true, mediaManager's faceTracker pipeline drives
   * combinedProcessor's zoom/pan from BlazeFace detections. Same state
   * is shared between DM calls and voice channels — there's only ever
   * one active local camera publication, so it doesn't make sense to
   * track per-surface.
   *
   * Three fields, split so the saved choice survives background
   * suppression (state machine in stores/autoFaceCenterCoupling.ts):
   *  - autoFaceCenterPref: the user's explicit long-term choice,
   *    persisted to localStorage('autoFaceCenter'), default ON.
   *  - autoFaceCenter: the EFFECTIVE state right now — what the button
   *    shows and what the tracker does. Runtime only.
   *  - autoFaceCenterSuppressedByBg: effective is off only because a
   *    background effect is active. Runtime only, never persisted.
   */
  autoFaceCenterPref: boolean
  autoFaceCenter: boolean
  autoFaceCenterSuppressedByBg: boolean
  peerVolumes: Record<string, number>
  /**
   * Master output volume (0..1), applied as a multiplicative gain on
   * top of any per-peer setting. Replaces the per-tile slider that
   * was hard to use and broke whenever participants reordered. Survives
   * across sessions via localStorage so the user doesn't have to
   * re-pick their preferred level every time.
   */
  masterVolume: number
  /**
   * Privacy save-slot for the DM↔Channel bridge feature.
   * - `null` — we are NOT in combined "channel + DM both connected"
   *   state. Privacy gate is dormant.
   * - `boolean` — the channel mic's `isMicrophoneEnabled` value at the
   *   moment we transitioned INTO combined state. The privacy gate
   *   auto-mutes channel mic right after saving. On exit from combined
   *   state we set channel mic back to this value.
   *
   * Read freshly via `useVoiceStore.getState().savedChannelMicEnabledBeforeDm`
   * inside async callbacks — closure capture can see a stale snapshot
   * across the privacy gate's transitions.
   */
  savedChannelMicEnabledBeforeDm: boolean | null
  setSavedChannelMicEnabledBeforeDm: (v: boolean | null) => void
  peerVideoHidden: Record<string, boolean>
  // Per-userId "this remote camera tile is scrolled off-screen in the grid"
  // flag, set by each tile's IntersectionObserver. Folded into the SFU
  // subscription decision in VoiceManager (off-screen ⇒ unsubscribe ⇒ stop
  // decoding) so a big grid doesn't decode 20+ VP9 streams at once — the
  // 2026-05-22 camera-flicker root cause. Transient; never persisted.
  peerVideoOffscreen: Record<string, boolean>
  /**
   * OSPROD-57: per-userId in-flight lock for the peer-video toggle.
   * Rapid clicks on the per-tile camera-hide button used to fire
   * setSubscribed() promises that overlapped — the SFU saw a stream
   * of subscribe/unsubscribe events, the final state was non-deterministic,
   * and the camera "turned off then on again" or didn't change at all.
   * Toggle action drops clicks while a userId is in this set, button
   * reads it for visible disabled / spinner state.
   */
  peerVideoToggleInFlight: Set<string>
  // Set of LiveKit participant identities that currently have their hand
  // raised. Ephemeral — cleared on leaveChannel and on local raisedHand
  // toggle. Powers the Zoom-style "🖐 wants to speak" indicator.
  raisedHands: Set<string>
  // OSPROD-61: identities currently flagged "Be right back". Same
  // ephemeral / data-channel-broadcast model as raisedHands — toggled
  // from the reactions picker, surfaces an hourglass overlay on the
  // participant's tile so other speakers know they stepped away.
  awayParticipants: Set<string>
  // Active floating emoji reactions keyed by participant identity. Each
  // entry is a small queue of bursts currently animating over that
  // participant's tile. Self-pruned by setTimeout — see pushReaction.
  participantReactions: Record<string, LiveReaction[]>
  viewMode: 'grid' | 'speaker'
  transcripts: TranscriptEntry[]
  // OSPROD-295 pagination state. `transcriptsTotal` is the server's real
  // count for this occupancy (the "N entries" label used to show the length
  // of the truncated array, which is why it froze at 200); `hasMore` says
  // whether anything older exists to scroll up into.
  transcriptsTotal: number | null
  transcriptsHasMore: boolean
  transcriptsLoadingOlder: boolean
  // Bumped by requestOlderTranscripts(); useTranscription owns the actual
  // socket emit, so the panel never touches the socket directly (repo rule:
  // listen/emit in stores and hooks, not components).
  transcriptsLoadOlderSeq: number
  // Currently-streaming utterances keyed by speakerId. At most one per
  // speaker. Cleared when the corresponding `transcript:new` arrives or
  // when leaving the channel.
  livePartials: Record<string, LivePartial>
  currentSpeakerId: string | null
  previousSpeakerId: string | null
  pinnedPrimaryId: string | null
  pinnedSecondaryId: string | null
  /**
   * OSPROD-64: ids of participants currently pinned in the Grid view.
   * Independent of pinnedPrimaryId / pinnedSecondaryId — Speaker mode
   * keeps using its own two slots untouched. Capped at N-1 where N is
   * the count of self + remote participants (synthetic screen-share-only
   * tiles don't count). Ephemeral — cleared on joinChannel / leaveChannel
   * and pruned in removeParticipant.
   */
  pinnedUserIds: Set<string>
  togglePin: (userId: string) => void
  clearPins: () => void
  isChannelViewVisible: boolean
  /**
   * OSPROD-60: hide the local self-view tile in the voice grid. Camera
   * keeps publishing — other participants still see us — only the local
   * tile is filtered out. Persisted via its own localStorage key so the
   * preference carries across joins / refreshes.
   */
  isSelfViewHidden: boolean
  setSelfViewHidden: (hidden: boolean) => void
  // Which tab the right-side voice panel shows. Survives across joins
  // so a user who prefers Chat keeps seeing Chat. Persisted via the
  // existing voiceStore localStorage blob.
  sidePanelTab: 'transcript' | 'chat' | 'activity'
  setSidePanelTab: (tab: 'transcript' | 'chat' | 'activity') => void
  // OSPROD-73: collapse state for the bottom chat/transcript strip in
  // the voice room. When true, only the tab bar (with the chat unread
  // badge + the un-collapse chevron) is rendered, freeing the vertical
  // space for the voice grid above. Persisted in localStorage so a
  // user who prefers a maximised grid keeps it across joins.
  isSidePanelCollapsed: boolean
  setSidePanelCollapsed: (collapsed: boolean) => void
  toggleSidePanelCollapsed: () => void
  // OSPROD-115: user-resizable height (px) of the bottom chat/transcript
  // strip during a call. Persisted in localStorage and shared across the
  // grid / speaker / focused layouts so the chosen size survives mode
  // switches and reloads. Only applies when the panel is expanded — the
  // collapsed state still shows just the tab bar.
  sidePanelHeight: number
  setSidePanelHeight: (height: number) => void
  // OSPROD-52: number of unseen text messages that arrived in the
  // currently joined voice channel since the user last had the Chat
  // tab in focus. Drives the badge on the Chat tab in the voice side
  // panel. Reset to 0 when:
  //   - the user opens the Chat tab (setSidePanelTab('chat')),
  //   - the user joins or leaves a voice channel (a fresh session),
  //   - clearSidePanelChatUnread() is called explicitly.
  sidePanelChatUnread: number
  bumpSidePanelChatUnread: () => void
  clearSidePanelChatUnread: () => void
  // Voice activity log for the currently joined channel's open session.
  // Appended live from voice:activity socket pushes; backfilled (merged)
  // from voice:activity:list. Cleared on join/leave. Capped at 500.
  sessionActivity: ActivityEvent[]
  addActivityEvent: (event: ActivityEvent) => void
  setSessionActivity: (events: ActivityEvent[]) => void
  setAudioInputDevice: (deviceId: string | null) => void
  setAudioOutputDevice: (deviceId: string | null) => void
  setVideoInputDevice: (deviceId: string | null) => void
  setSelfMirror: (on: boolean) => void
  toggleNoiseSuppression: () => void
  setVideoQuality: (quality: 'auto' | 'low' | 'hd' | 'fhd') => void
  // OSPROD-92: persist the user's last camera ON/OFF intent so reconnect /
  // page-refresh paths can restore it. Called by livekitStore.toggleVideo.
  setLastCameraEnabled: (enabled: boolean) => void
  /** opts.restoreFrame:false — the caller applies its own zoom/pan right
   *  after (CameraZoomPicker manual adjust), so disabling must NOT restore
   *  the profile-saved frame over it (OSPROD-232). */
  setAutoFaceCenter: (enabled: boolean, opts?: { restoreFrame?: boolean }) => void
  /** Notify the auto-frame coupling that the background turned on/off. */
  syncBackgroundCoupling: (bgActive: boolean) => void
  setIncomingQuality: (q: IncomingQuality) => void
  joinChannel: (channelId: string, serverId?: string, opts?: { micPolicy?: 'unmute' }) => void
  leaveChannel: (opts?: { deliberate?: boolean }) => void
  setPendingAutoJoin: (channelId: string | null, micPolicy?: 'unmute') => void
  setParticipants: (participants: VoiceState[]) => void
  addParticipant: (participant: VoiceState) => void
  removeParticipant: (userId: string) => void
  // OSPROD-55: per-userId timestamp of the last LOCAL update we applied
  // (delta event arrival, add, remove). Used by applyParticipantsSnapshot
  // to detect when a snapshot is older than a delta we already saw and
  // would otherwise wipe out the delta.
  participantsLastUpdate: Record<string, number>
  // OSPROD-55: bump the local-update timestamp for a userId. Public so
  // VoiceManager can mark deltas it applies via direct setState (the
  // socket on('voice:state:changed') path doesn't go through the
  // add/remove actions because it has its own dedupe via
  // knownParticipantsRef and shouldn't trigger join/leave sounds).
  markParticipantUpdated: (userId: string) => void
  // OSPROD-55: apply a snapshot from the server with merge semantics.
  // Snapshot is treated as "ground truth as of fetchedAt"; any local
  // entries whose lastUpdate > fetchedAt survive the apply, and any
  // snapshot entries for users we removed AFTER fetchedAt are dropped.
  // This kills the race where a slow snapshot fetch arrives after a
  // join/leave delta and wholesale-replaces it.
  applyParticipantsSnapshot: (snapshot: VoiceState[], fetchedAt: number) => void
  setPeerVolume: (userId: string, volume: number) => void
  togglePeerMute: (userId: string) => void
  setMasterVolume: (volume: number) => void
  togglePeerVideoHidden: (userId: string) => Promise<void>
  setPeerVideoHidden: (userId: string, hidden: boolean) => void
  setPeerVideoOffscreen: (userId: string, offscreen: boolean) => void
  loadPeerCustomizations: (custom: Record<string, { volume?: number; videoHidden?: boolean }>) => void
  setViewMode: (mode: 'grid' | 'speaker') => void
  addTranscript: (entry: TranscriptEntry) => void
  setTranscripts: (transcripts: TranscriptEntry[]) => void
  // OSPROD-267: merge a history backfill (voice:getTranscripts response)
  // into whatever live entries already arrived — dedupe by id, never
  // overwrite. Used on panel mount + socket reconnect so late joiners /
  // reconnecting tabs converge to the same transcript everyone else sees.
  mergeTranscripts: (entries: TranscriptEntry[]) => void
  prependTranscripts: (entries: TranscriptEntry[]) => void
  requestOlderTranscripts: () => void
  setTranscriptPageState: (patch: Partial<Pick<VoiceStoreState, 'transcriptsTotal' | 'transcriptsHasMore' | 'transcriptsLoadingOlder'>>) => void
  clearTranscripts: () => void
  // Patch a transcript's cleanedText in place (LLM cleanup landed).
  setTranscriptCleanedText: (id: string, cleanedText: string) => void
  // Replace (or insert) the live partial for a given speaker. Called by
  // the `transcript:partial` socket handler.
  setLivePartial: (entry: LivePartial) => void
  // Drop the partial for a given speaker — called when their utterance
  // gets committed (via `transcript:new`) or when they leave the room.
  clearLivePartial: (speakerId: string) => void
  setCurrentSpeakerId: (id: string | null) => void
  setPreviousSpeakerId: (id: string | null) => void
  setPinnedIds: (primaryId: string | null, secondaryId: string | null) => void
  setChannelViewVisible: (visible: boolean) => void
  setRaisedHand: (identity: string, raised: boolean) => void
  toggleLocalRaisedHand: () => void
  setAway: (identity: string, away: boolean) => void
  // Set the LOCAL participant's away flag without cascading to mic/deafen.
  // Used by the auth-store mute path (mute → BRB) so the cascade only
  // ever runs in one direction at a time, and by the explicit BRB
  // toggle's own state-update step.
  setLocalAway: (away: boolean) => void
  toggleLocalAway: () => void
  // Append a burst over a participant's tile (no broadcast — used from
  // the data-channel receiver and as the local-echo path of sendReaction).
  pushReaction: (identity: string, emoji: string) => void
  // Send the local user's reaction: rate-limited, local echo + broadcast
  // over LiveKit's data channel. Returns false when blocked by cooldown.
  sendReaction: (emoji: string) => boolean
}

function getCurrentUserId(): string | undefined {
  // The live DB user id from authStore is the source of truth — it's
  // the same value VoiceChannelView's self-tile keys by
  // (`userId: currentUser?.id`). The previous implementation read
  // `localStorage.user.id`, but nothing in the codebase writes that
  // key, so it always returned undefined and the local-echo fell
  // through to the LiveKit identity (=username), which doesn't match
  // the self-tile's DB-id keying — so the user never saw their own
  // raised hand or reaction. Falls back to the legacy probe so test
  // suites that pre-seed `localStorage.user` (without booting
  // authStore) still resolve to an id.
  const fromAuth = useAuthStore.getState().user?.id
  if (fromAuth) return fromAuth
  try {
    return JSON.parse(localStorage.getItem('user') || '{}').id
  } catch {
    return undefined
  }
}

// Module-scoped so it doesn't sit in the Zustand state and trigger
// re-renders. We only need it for the cooldown check inside
// sendReaction, never for UI.
let lastReactionSentAt = 0
// Test-only escape hatch — production callers never need this. Without
// it the cooldown leaks across vitest tests because they share the
// module instance.
export function __resetReactionThrottleForTests(): void {
  lastReactionSentAt = 0
}

export const useVoiceStore = create<VoiceStoreState>((set, get) => ({
  currentChannelId: typeof window !== 'undefined' ? localStorage.getItem('currentChannelId') : null,
  pendingAutoJoinChannelId: null,
  pendingAutoJoinMicPolicy: null,
  currentServerId: typeof window !== 'undefined' ? localStorage.getItem('currentServerId') : null,
  joinedAt: null,
  isVideoOn: false,
  isScreenSharing: false,
  isScreenSharingAudio: false,
  participants: [],
  bannedInfo: null,
  setBannedInfo: (bannedInfo) => set({ bannedInfo }),
  voiceActiveElsewhere: null,
  setVoiceActiveElsewhere: (voiceActiveElsewhere) => set({ voiceActiveElsewhere }),
  audioInputDeviceId: typeof window !== 'undefined' ? localStorage.getItem('audioInputDeviceId') : null,
  audioOutputDeviceId: typeof window !== 'undefined' ? localStorage.getItem('audioOutputDeviceId') : null,
  videoInputDeviceId: typeof window !== 'undefined' ? localStorage.getItem('videoInputDeviceId') : null,
  selfMirror: typeof window !== 'undefined' ? localStorage.getItem('selfMirror') !== 'false' : true,
  isNoiseSuppressionOn: typeof window !== 'undefined' ? localStorage.getItem('noiseSuppression') !== 'false' : true,
  videoQuality: (typeof window !== 'undefined'
    ? (localStorage.getItem('videoQuality') as 'auto' | 'low' | 'hd' | 'fhd' | null)
    : null) || 'auto',
  // OSPROD-92: read persisted camera intent. Default OFF for first-time
  // users — only restores if the user has flipped it on in a past session
  // and we wrote `voiceCameraOn=true` to localStorage.
  lastCameraEnabled: typeof window !== 'undefined' && localStorage.getItem('voiceCameraOn') === 'true',
  incomingQuality: ((): IncomingQuality => {
    if (typeof window === 'undefined') return 'auto'
    const v = localStorage.getItem('incomingVideoQuality')
    return v === 'low' || v === 'medium' || v === 'auto' ? v : 'auto'
  })(),
  videoSpeakersOnly: readVideoSpeakersOnly(),
  // Default-on so new users get nicely framed automatically; persisted
  // to localStorage so a user who explicitly turns it off doesn't have to
  // re-disable it every session. `autoFaceCenter` (effective) starts equal
  // to the pref; the camera-on join path + background coupling refine it.
  autoFaceCenterPref: typeof window !== 'undefined' ? localStorage.getItem('autoFaceCenter') !== 'false' : true,
  autoFaceCenter: typeof window !== 'undefined' ? localStorage.getItem('autoFaceCenter') !== 'false' : true,
  autoFaceCenterSuppressedByBg: false,
  peerVolumes: {},
  masterVolume: typeof window !== 'undefined'
    ? (() => {
        const raw = localStorage.getItem('masterVolume')
        const parsed = raw == null ? NaN : Number.parseFloat(raw)
        return Number.isFinite(parsed) ? Math.max(0, Math.min(1, parsed)) : 1
      })()
    : 1,
  savedChannelMicEnabledBeforeDm: null,
  peerVideoHidden: {},
  peerVideoOffscreen: {},
  peerVideoToggleInFlight: new Set<string>(),
  viewMode: typeof window !== 'undefined' ? (localStorage.getItem('voiceViewMode') as 'grid' | 'speaker') || 'grid' : 'grid',
  transcripts: [],
  transcriptsTotal: null,
  transcriptsHasMore: false,
  transcriptsLoadingOlder: false,
  transcriptsLoadOlderSeq: 0,
  livePartials: {},
  currentSpeakerId: null,
  previousSpeakerId: null,
  pinnedPrimaryId: null,
  pinnedSecondaryId: null,
  pinnedUserIds: new Set<string>(),
  isChannelViewVisible: false,
  isSelfViewHidden: typeof window !== 'undefined'
    ? localStorage.getItem('voiceSelfViewHidden') === 'true'
    : false,
  // Default to Transcript so existing users see no behaviour change on
  // first load; switching to Chat sticks via localStorage.
  sidePanelTab: typeof window !== 'undefined'
    ? ((localStorage.getItem('voiceSidePanelTab') as 'transcript' | 'chat' | 'activity') || 'transcript')
    : 'transcript',
  // OSPROD-100: transcript/chat panel collapsed (hidden) by default on
  // room entry. Respect an explicit user preference — only an explicit
  // 'false' (user opened it) keeps it expanded; no value or 'true' → hidden.
  isSidePanelCollapsed: typeof window !== 'undefined'
    ? localStorage.getItem('voiceSidePanelCollapsed') !== 'false'
    : true,
  // OSPROD-115: persisted side-panel height; default 288 (the old fixed
  // h-72) so existing users see no change until they drag the handle.
  sidePanelHeight: typeof window !== 'undefined'
    ? Number(localStorage.getItem('voiceSidePanelHeight')) || 288
    : 288,
  sidePanelChatUnread: 0,
  sessionActivity: [],
  raisedHands: new Set<string>(),
  awayParticipants: new Set<string>(),
  participantReactions: {},
  // OSPROD-55: see interface comment. Empty on boot — every entry gets
  // populated as deltas / snapshots arrive.
  participantsLastUpdate: {},

  joinChannel: (channelId, serverId, opts) => {
    sounds.selfJoin()
    // Launch policy (2026-06-09): EVERY deliberate channel join starts MUTED
    // with the CAMERA OFF, regardless of what was enabled in a previous
    // channel. The user turns them on themselves. We reset the INTENT sources —
    // `user.settings.isMuted` (read by livekitStore.connect for the initial mic
    // state AND by the 5s health-check reconciler as `expectedMuted`) and
    // `lastCameraEnabled` (the camera equivalent) — so connect() comes up off
    // AND the reconciler keeps it off until the user acts.
    //
    // The mute MUST be PERSISTED, not in-memory-only: the reconciler follows
    // settings.isMuted, and a later fetchUser/updateMe overwriting user.settings
    // with the server's stale isMuted=false made the reconciler re-ENABLE the
    // mic mid-call (prod health-audio-desync unmutes, 2026-06-09). muteForJoin
    // does the optimistic set + background persist. The camera intent
    // (lastCameraEnabled) is localStorage-backed, so it has no server-revert
    // hole. This lives in joinChannel (the deliberate-join chokepoint);
    // socket/page reconnects call livekitStore.connect() directly and bypass
    // this, so a network blip preserves mic/camera mid-call (invariants #20/#92).
    //
    // EXCEPTION — micPolicy 'unmute' (phone-admit join): admitting a PSTN
    // caller = answering the phone. The SIP gateway answers the phone leg
    // only once it can subscribe to a remote AUDIO track, and a muted
    // participant publishes no track at all — so this join comes up live.
    if (opts?.micPolicy === 'unmute') {
      useAuthStore.getState().unmuteForJoin()
    } else {
      useAuthStore.getState().muteForJoin()
    }
    get().setLastCameraEnabled(false)
    if (typeof window !== 'undefined') {
      localStorage.setItem('currentChannelId', channelId)
      if (serverId) localStorage.setItem('currentServerId', serverId)
      else localStorage.removeItem('currentServerId')
    }
    // Clear stale transcripts from any previous session — leaveChannel
    // already does this, but a hot-reload/crash recovery path may leave
    // the store with old entries. Joining a fresh room should always show
    // an empty transcription panel until somebody actually speaks.
    set({
      currentChannelId: channelId,
      currentServerId: serverId || null,
      joinedAt: Date.now(),
      transcripts: [],
      transcriptsTotal: null,
      transcriptsHasMore: false,
      transcriptsLoadingOlder: false,
      currentSpeakerId: null,
      previousSpeakerId: null,
      // OSPROD-52: every fresh voice session starts with a clean badge.
      sidePanelChatUnread: 0,
      // OSPROD-64: pins are per-session — fresh room starts with none.
      pinnedUserIds: new Set<string>(),
      // OSPROD-55: drop participant tombstones from the previous session
      // so a re-join doesn't carry stale "this user was here" timestamps
      // that would override the fresh snapshot.
      participantsLastUpdate: {},
      sessionActivity: [],
    })
  },

  setPendingAutoJoin: (pendingAutoJoinChannelId, micPolicy) => set({
    pendingAutoJoinChannelId,
    pendingAutoJoinMicPolicy: pendingAutoJoinChannelId ? (micPolicy ?? null) : null,
  }),

  leaveChannel: (opts) => {
    // Deliberate disconnect (the user clicked the button) tones immediately;
    // involuntary leaves keep the reconnect grace so network blips stay
    // silent (see sounds.selfLeave).
    sounds.selfLeave(!!opts?.deliberate)
    const { currentChannelId, participants } = get()
    if (currentChannelId) {
      getSocket().emit('voice:leave', { channelId: currentChannelId })
    }
    if (typeof window !== 'undefined') {
      localStorage.removeItem('currentChannelId')
      localStorage.removeItem('currentServerId')
    }
    set({
      currentChannelId: null,
      currentServerId: null,
      joinedAt: null,
      isVideoOn: false,
      isScreenSharing: false,
      isScreenSharingAudio: false,
      participants: participants.filter((p) => p.channelId !== currentChannelId),
      transcripts: [],
      transcriptsTotal: null,
      transcriptsHasMore: false,
      transcriptsLoadingOlder: false,
      livePartials: {},
      currentSpeakerId: null,
      previousSpeakerId: null,
      pinnedPrimaryId: null,
      pinnedSecondaryId: null,
      pinnedUserIds: new Set<string>(),
      raisedHands: new Set<string>(),
      // OSPROD-61: away flags are per-session — re-joining shouldn't
      // inherit stale "be right back" markers from the previous room.
      awayParticipants: new Set<string>(),
      // OSPROD-52: badge is per-session — drop on leave so the next
      // join doesn't inherit a stale count from a previous room.
      sidePanelChatUnread: 0,
      participantReactions: {},
      // OSPROD-55: clear update timestamps too — next room is a fresh
      // session, no tombstones from the room we're leaving should
      // affect snapshot merges over there.
      participantsLastUpdate: {},
      sessionActivity: [],
    })
  },

  setAudioInputDevice: (deviceId) => {
    set({ audioInputDeviceId: deviceId })
    if (typeof window !== 'undefined') {
      if (deviceId) localStorage.setItem('audioInputDeviceId', deviceId)
      else localStorage.removeItem('audioInputDeviceId')
    }
  },

  setVideoInputDevice: (deviceId) => {
    set({ videoInputDeviceId: deviceId })
    if (typeof window !== 'undefined') {
      if (deviceId) localStorage.setItem('videoInputDeviceId', deviceId)
      else localStorage.removeItem('videoInputDeviceId')
    }
  },

  setSelfMirror: (on) => {
    set({ selfMirror: on })
    if (typeof window !== 'undefined') localStorage.setItem('selfMirror', String(on))
  },

  setAudioOutputDevice: (deviceId) => {
    const prev = get().audioOutputDeviceId
    set({ audioOutputDeviceId: deviceId })
    if (typeof window !== 'undefined') {
      if (deviceId) localStorage.setItem('audioOutputDeviceId', deviceId)
      else localStorage.removeItem('audioOutputDeviceId')
    }
    if (deviceId && deviceId !== prev) emitVoiceActivity(get().currentChannelId, 'device_output')
  },

  toggleNoiseSuppression: () => {
    const next = !get().isNoiseSuppressionOn
    set({ isNoiseSuppressionOn: next })
    if (typeof window !== 'undefined') {
      localStorage.setItem('noiseSuppression', String(next))
    }
    // If we're already in a voice channel, republish the mic with the new
    // constraint — otherwise the toggle would only take effect on the
    // *next* connect. setMicrophoneEnabled(true, opts) replaces the
    // existing track in place; LiveKit handles the swap.
    const room = useLivekitStore.getState().room
    if (room && room.localParticipant.isMicrophoneEnabled) {
      mediaManager
        .setMicrophoneEnabled(room, true, { noiseSuppression: next })
        .catch((err) => console.error('Failed to apply noise suppression:', err))
    }
  },

  // (setDenoiserBackend + setAecMode dropped in 1.1.177A with the DFN
  // backend itself. RNNoise is the only in-app denoiser; there's no UI
  // backend chooser anymore, and the DFN AEC pre-stage is gone.)

  setLastCameraEnabled: (enabled) => {
    if (get().lastCameraEnabled === enabled) return
    set({ lastCameraEnabled: enabled })
    if (typeof window !== 'undefined') {
      try { localStorage.setItem('voiceCameraOn', String(enabled)) } catch {}
    }
  },

  setVideoQuality: (quality) => {
    const prev = get().videoQuality
    set({ videoQuality: quality })
    if (typeof window !== 'undefined') {
      localStorage.setItem('videoQuality', quality)
    }
    if (prev === quality) return
    // Apply live to whichever LiveKit room is active — voice channel,
    // DM call, or both (Telegram-style call overlays use the DM call
    // store). restartCameraForQuality is a no-op when the camera is
    // off; in that case the next camera-on event will pick up the new
    // preset because mediaManager.appliedVideoQuality has been reset.
    const channelRoom = useLivekitStore.getState().room
    if (channelRoom) {
      mediaManager.restartCameraForQuality(channelRoom).catch((err) => {
        console.error('Failed to apply video quality (channel room):', err)
      })
    }
    const dmRoom = useDmCallStore.getState().room
    if (dmRoom) {
      mediaManager.restartCameraForQuality(dmRoom).catch((err) => {
        console.error('Failed to apply video quality (dm room):', err)
      })
    }
  },

  setAutoFaceCenter: (enabled, opts) => {
    // Rule 1 (autoFaceCenterCoupling): a manual toggle is the user's
    // explicit, remembered choice — it writes the pref AND clears any
    // background auto-suppression. So enabling auto-frame while a
    // background is on means a later "background off" won't re-toggle it.
    const next = applyManualToggle(
      {
        pref: get().autoFaceCenterPref,
        effective: get().autoFaceCenter,
        suppressedByBg: get().autoFaceCenterSuppressedByBg,
      },
      enabled,
    )
    set({
      autoFaceCenterPref: next.pref,
      autoFaceCenter: next.effective,
      autoFaceCenterSuppressedByBg: next.suppressedByBg,
    })
    if (typeof window !== 'undefined') {
      localStorage.setItem('autoFaceCenter', String(next.pref))
    }
    // Apply against whichever room currently owns the camera publish —
    // mediaManager is room-aware, so for the very rare case of both
    // rooms being up at once we route to the DM call first (which
    // takes the local camera per dmCallStore.connect's mutex with
    // livekitStore.connect via OSPROD-76).
    const dmRoom = useDmCallStore.getState().room
    const channelRoom = useLivekitStore.getState().room
    const room = dmRoom ?? channelRoom
    mediaManager.setAutoFaceCenter(room, next.effective, opts).catch((err) => {
      console.error('Failed to toggle auto face center:', err)
      // Only the apply failed — keep the saved pref as the user asked,
      // but reflect that the tracker is not actually running.
      set({ autoFaceCenter: false })
    })
  },

  syncBackgroundCoupling: (bgActive) => {
    // Rules 2 & 3 (autoFaceCenterCoupling): a background turning ON
    // suppresses auto-frame; turning OFF restores it ONLY when the off
    // was an automatic suppression. Called by BackgroundEffectPicker
    // whenever the effect changes (and on mount).
    const cur = {
      pref: get().autoFaceCenterPref,
      effective: get().autoFaceCenter,
      suppressedByBg: get().autoFaceCenterSuppressedByBg,
    }
    const next = applyBackgroundChange(cur, bgActive)
    if (next.effective === cur.effective && next.suppressedByBg === cur.suppressedByBg) {
      return // nothing changed — avoids tracker churn when switching between blur presets
    }
    set({ autoFaceCenter: next.effective, autoFaceCenterSuppressedByBg: next.suppressedByBg })
    // Drive the tracker only when the effective state actually flipped and a
    // room owns the camera. With no room the flags suffice — the next
    // camera-on recomputes via computeJoinState in mediaManager.
    if (next.effective !== cur.effective) {
      const room = useDmCallStore.getState().room ?? useLivekitStore.getState().room
      if (room) {
        mediaManager.setAutoFaceCenter(room, next.effective).catch((err) => {
          console.error('Failed to sync auto-frame with background:', err)
        })
      }
    }
  },

  // "Видео только говорящих": VoiceManager owns the actual SFU
  // (un)subscription; this just persists the intent and notifies it.
  setVideoSpeakersOnly: (on) => {
    set({ videoSpeakersOnly: on })
    if (typeof window !== 'undefined') {
      localStorage.setItem('videoSpeakersOnly', on ? 'true' : 'false')
    }
  },

  forceMutedChannels: new Set<string>(),
  isForceMutedHere: (channelId) => !!channelId && get().forceMutedChannels.has(channelId),
  setForceMutedHere: (channelId, locked) => set((prev) => {
    const next = new Set(prev.forceMutedChannels)
    if (locked) next.add(channelId)
    else next.delete(channelId)
    return { forceMutedChannels: next }
  }),

  setIncomingQuality: (q) => {
    set({ incomingQuality: q })
    if (typeof window !== 'undefined') {
      localStorage.setItem('incomingVideoQuality', q)
    }
    // Setting setVideoQuality always lifts/replaces any existing cap;
    // for 'auto' we explicitly set HIGH (= effective no-cap) so a user
    // switching back from MEDIUM/LOW immediately gets the high layer
    // again. Each VoiceTile's setVideoDimensions will then override
    // this on its next ResizeObserver tick to drive the layer from
    // the actual rendered size.
    const lkQ = getIncomingLkQuality(q)
    // Big-room auto-cap owns the ceiling while engaged (>20 camera
    // publishers force LOW) — a manual quality change must not lift it
    // mid-call and let 25 HIGH streams burst in. The preference is
    // persisted above and re-applies once the cap releases.
    if (useLivekitStore.getState().bigRoomForcedLow) return
    const apply = (room: Room | null) => {
      if (!room) return
      room.remoteParticipants.forEach((p) => {
        p.trackPublications.forEach((pub) => {
          if (pub.kind !== Track.Kind.Video) return
          try {
            ;(pub as RemoteTrackPublication).setVideoQuality(lkQ)
          } catch {}
        })
      })
    }
    apply(useLivekitStore.getState().room)
    apply(useDmCallStore.getState().room)
  },

  setParticipants: (participants) => set(() => {
    const now = Date.now()
    const lastUpdate: Record<string, number> = {}
    for (const p of participants) lastUpdate[p.userId] = now
    return { participants, participantsLastUpdate: lastUpdate }
  }),

  markParticipantUpdated: (userId) => set((state) => ({
    participantsLastUpdate: { ...state.participantsLastUpdate, [userId]: Date.now() },
  })),

  addParticipant: (participant) => {
    const { participants, participantsLastUpdate } = get()
    const filtered = participants.filter((p) => p.userId !== participant.userId)
    set({
      participants: [...filtered, participant],
      participantsLastUpdate: { ...participantsLastUpdate, [participant.userId]: Date.now() },
    })
    if (participant.userId !== getCurrentUserId()) {
      sounds.otherJoin()
    }
  },

  removeParticipant: (userId) => {
    const { participants, pinnedUserIds, participantsLastUpdate } = get()
    const nextPins = pinnedUserIds.has(userId)
      ? new Set(Array.from(pinnedUserIds).filter((id) => id !== userId))
      : pinnedUserIds
    set({
      participants: participants.filter((p) => p.userId !== userId),
      pinnedUserIds: nextPins,
      // Keep the timestamp around — it's the "tombstone" that prevents a
      // stale snapshot from re-adding a user we just removed.
      participantsLastUpdate: { ...participantsLastUpdate, [userId]: Date.now() },
    })
    if (userId !== getCurrentUserId()) {
      sounds.otherLeave()
    }
  },

  applyParticipantsSnapshot: (snapshot, fetchedAt) => {
    const { participants, participantsLastUpdate } = get()
    // 1. Drop snapshot entries for users we touched LOCALLY after the
    //    snapshot was taken (e.g. user left between fetch start and
    //    response — the leave delta already updated us, snapshot would
    //    re-add the ghost).
    const acceptableSnapshot = snapshot.filter((s) => {
      const ts = participantsLastUpdate[s.userId]
      return !ts || ts <= fetchedAt
    })
    const acceptedIds = new Set(acceptableSnapshot.map((s) => s.userId))

    // 2. Keep local entries the snapshot doesn't have IF we got a delta
    //    for them after the snapshot was taken (they joined while the
    //    fetch was in flight — snapshot didn't see them yet). Drop any
    //    other local entries: snapshot is the new ground truth.
    const localKept = participants.filter((p) => {
      if (acceptedIds.has(p.userId)) return false
      const ts = participantsLastUpdate[p.userId] ?? 0
      return ts > fetchedAt
    })

    const merged = [...acceptableSnapshot, ...localKept]
    const nextLastUpdate: Record<string, number> = { ...participantsLastUpdate }
    for (const p of acceptableSnapshot) {
      // Snapshot entries: stamp at the fetch time, NOT now. That way a
      // late delta we haven't applied yet (lastUpdate would be > now if
      // it arrives after this) still wins over the snapshot.
      nextLastUpdate[p.userId] = Math.max(nextLastUpdate[p.userId] ?? 0, fetchedAt)
    }
    set({ participants: merged, participantsLastUpdate: nextLastUpdate })
  },

  setPeerVolume: (userId, volume) => {
    set({ peerVolumes: { ...get().peerVolumes, [userId]: volume } })
  },

  togglePeerMute: (userId) => {
    const current = get().peerVolumes[userId] ?? 1
    set({ peerVolumes: { ...get().peerVolumes, [userId]: current === 0 ? 1 : 0 } })
  },

  setMasterVolume: (volume) => {
    const clamped = Math.max(0, Math.min(1, volume))
    set({ masterVolume: clamped })
    try { localStorage.setItem('masterVolume', String(clamped)) } catch { /* private mode */ }
  },

  setSavedChannelMicEnabledBeforeDm: (v) => set({ savedChannelMicEnabledBeforeDm: v }),

  /**
   * OSPROD-57: serialised peer-video toggle.
   *
   * 1. Drop the click if a previous toggle for the same userId is still
   *    in flight — without this, the SFU saw overlapping subscribe /
   *    unsubscribe events and the camera ended up in a non-deterministic
   *    state ("turns off then back on").
   * 2. Flip local store state up-front (optimistic) so the UI flips
   *    instantly.
   * 3. AWAIT the LiveKit subscription change. The reactive apply() in
   *    VoiceManager is a fallback for initial sync / new tracks, but the
   *    explicit toggle goes through this serialised path.
   * 4. On error, revert state and surface a toast so the user sees why
   *    the camera popped back.
   */
  togglePeerVideoHidden: async (userId) => {
    const inFlight = get().peerVideoToggleInFlight
    if (inFlight.has(userId)) return  // ignore rapid re-clicks

    const next = !get().peerVideoHidden[userId]
    const newInFlight = new Set(inFlight)
    newInFlight.add(userId)
    set({
      peerVideoToggleInFlight: newInFlight,
      peerVideoHidden: { ...get().peerVideoHidden, [userId]: next },
    })

    try {
      const room = useLivekitStore.getState().room
      if (room) {
        const participant = room.remoteParticipants.get(userId)
        const pub = participant?.getTrackPublication(Track.Source.Camera) as
          | RemoteTrackPublication
          | undefined
        if (pub) {
          await pub.setSubscribed(!next)
        }
      }
    } catch (err) {
      console.warn('[voice] togglePeerVideoHidden failed, reverting:', err)
      set({
        peerVideoHidden: { ...get().peerVideoHidden, [userId]: !next },
      })
    } finally {
      const updated = new Set(get().peerVideoToggleInFlight)
      updated.delete(userId)
      set({ peerVideoToggleInFlight: updated })
    }
  },

  setPeerVideoHidden: (userId, hidden) => {
    set({ peerVideoHidden: { ...get().peerVideoHidden, [userId]: hidden } })
  },

  setPeerVideoOffscreen: (userId, offscreen) => {
    // No-op on unchanged value so we don't churn a new object (→ VoiceManager
    // re-subscribe pass) on every IntersectionObserver tick.
    if (!!get().peerVideoOffscreen[userId] === offscreen) return
    set({ peerVideoOffscreen: { ...get().peerVideoOffscreen, [userId]: offscreen } })
  },

  loadPeerCustomizations: (custom) => {
    const volumes: Record<string, number> = {}
    const hidden: Record<string, boolean> = {}
    Object.entries(custom).forEach(([uid, c]) => {
      if (typeof c.volume === 'number') volumes[uid] = c.volume
      if (typeof c.videoHidden === 'boolean') hidden[uid] = c.videoHidden
    })
    set({ peerVolumes: volumes, peerVideoHidden: hidden })
  },

  setViewMode: (mode) => {
    set({ viewMode: mode })
    if (typeof window !== 'undefined') {
      localStorage.setItem('voiceViewMode', mode)
    }
  },

  addTranscript: (entry) => {
    // Cap at 200 entries (199 old + 1 new) — the panel scroll handles
    // older history, anything beyond 200 in memory is just churn. Also
    // drop the live partial for this speaker (if any): the server only
    // emits `transcript:new` after committing the burst, so any
    // matching partial is now stale.
    const next = { ...get().livePartials }
    if (entry.speakerId && next[entry.speakerId]) delete next[entry.speakerId]
    // Dedupe by id — when a partial gets force-finalized client-side
    // due to interruption (see setLivePartial below), we push it into
    // `transcripts` early. The server later emits the authoritative
    // `transcript:new` for the same id; replace in-place so we don't
    // render the burst twice.
    const transcripts = get().transcripts
    const idx = transcripts.findIndex((t) => t.id === entry.id)
    if (idx >= 0) {
      const merged = transcripts.slice()
      merged[idx] = entry
      set({ transcripts: merged, livePartials: next })
      return
    }
    // OSPROD-295: no more slice(-199). This cap hit FIRST — during a long
    // call, with no refresh involved — silently dropping the oldest line on
    // every new one, so a transcript could never grow past 200 no matter what
    // the server sent.
    set({
      transcripts: [...transcripts, entry],
      livePartials: next,
    })
  },

  setTranscripts: (transcripts) => {
    set({ transcripts })
  },

  mergeTranscripts: (entries) => {
    if (!entries || entries.length === 0) return
    const existing = get().transcripts
    // Live `transcript:new` entries win over backfilled copies of the
    // same line (they carry endTimestamp for bubble merging; the DB
    // backfill doesn't) — so only ADD ids we haven't seen.
    const seen = new Set(existing.map((t) => t.id))
    const fresh = entries.filter((e) => e && e.id && !seen.has(e.id))
    if (fresh.length === 0) return
    const merged = [...existing, ...fresh].sort((a, b) => a.timestamp - b.timestamp)
    set({ transcripts: merged })
  },

  // OSPROD-295: older page fetched by scrolling up. Kept separate from
  // mergeTranscripts so the live-append path stays untouched — this only
  // ever adds entries that sort BEFORE what we already hold, and the panel
  // compensates the scroll offset after it lands.
  prependTranscripts: (entries) => {
    if (!entries || entries.length === 0) return
    const existing = get().transcripts
    const seen = new Set(existing.map((t) => t.id))
    const fresh = entries.filter((e) => e && e.id && !seen.has(e.id))
    if (fresh.length === 0) return
    set({ transcripts: [...fresh, ...existing].sort((a, b) => a.timestamp - b.timestamp) })
  },

  setTranscriptPageState: (patch) => set(patch),

  requestOlderTranscripts: () => {
    const st = get()
    if (!st.transcriptsHasMore || st.transcriptsLoadingOlder) return
    if (st.transcripts.length === 0) return
    set({ transcriptsLoadingOlder: true, transcriptsLoadOlderSeq: st.transcriptsLoadOlderSeq + 1 })
  },

  clearTranscripts: () => set({ transcripts: [], livePartials: {}, transcriptsTotal: null, transcriptsHasMore: false, transcriptsLoadingOlder: false }),

  setTranscriptCleanedText: (id, cleanedText) => {
    const transcripts = get().transcripts
    const idx = transcripts.findIndex((t) => t.id === id)
    if (idx === -1) return
    const next = transcripts.slice()
    next[idx] = { ...next[idx], cleanedText }
    set({ transcripts: next })
  },

  setLivePartial: (entry) => {
    // Interruption: when a different speaker starts mid-utterance, the
    // user wants the previous speaker's bubble finalized right away
    // ("пишем от лица нового"). Move every other speaker's open partial
    // into the committed list and drop it from livePartials. The server
    // will eventually emit its own `transcript:new` for that burst with
    // the same id once the original speaker's silence-flush fires;
    // addTranscript dedupes by id so the bubble isn't rendered twice.
    const currentPartials = get().livePartials
    const transcripts = get().transcripts
    const promoted: TranscriptEntry[] = []
    const remaining: Record<string, LivePartial> = {}
    for (const [sid, p] of Object.entries(currentPartials)) {
      if (sid === entry.speakerId) continue // overwritten below
      promoted.push({
        id: p.id,
        speakerId: p.speakerId,
        speakerName: p.speakerName,
        text: p.text,
        timestamp: p.timestamp,
        // Carry the latest end-of-burst time we know about. If the
        // server hadn't emitted one yet (legacy session), fall back to
        // wall clock — the partial is being finalized RIGHT NOW.
        endTimestamp: p.endTimestamp ?? Date.now(),
      })
    }
    // Merge: prepend promoted items that aren't already in transcripts
    // (defensive — addTranscript dedupes too).
    let nextTranscripts = transcripts
    if (promoted.length > 0) {
      const seen = new Set(transcripts.map((t) => t.id))
      const fresh = promoted.filter((t) => !seen.has(t.id))
      if (fresh.length > 0) {
        // OSPROD-295: uncapped, like addTranscript/mergeTranscripts.
        nextTranscripts = [...transcripts, ...fresh]
      }
    }
    set({
      livePartials: { ...remaining, [entry.speakerId]: entry },
      transcripts: nextTranscripts,
    })
  },

  clearLivePartial: (speakerId) => {
    if (!get().livePartials[speakerId]) return
    const next = { ...get().livePartials }
    delete next[speakerId]
    set({ livePartials: next })
  },

  setCurrentSpeakerId: (id) => {
    if (get().currentSpeakerId === id) return
    set({ currentSpeakerId: id })
  },
  setPreviousSpeakerId: (id) => {
    if (get().previousSpeakerId === id) return
    set({ previousSpeakerId: id })
  },
  setPinnedIds: (primaryId, secondaryId) => {
    set({ pinnedPrimaryId: primaryId, pinnedSecondaryId: secondaryId })
  },
  togglePin: (userId) => {
    const { pinnedUserIds, participants } = get()
    const next = new Set(pinnedUserIds)
    if (next.has(userId)) {
      next.delete(userId)
    } else {
      // Cap: N-1 where N = real participants. Pinning everyone leaves no
      // unpinned tile and defeats the layout. Silently drop the click —
      // the UI button should already be disabled when we hit the cap, so
      // this is just defense-in-depth against a stale click.
      const cap = Math.max(0, participants.length - 1)
      if (next.size >= cap) return
      next.add(userId)
    }
    set({ pinnedUserIds: next })
  },
  clearPins: () => set({ pinnedUserIds: new Set<string>() }),
  addActivityEvent: (event) => {
    const key = (e: ActivityEvent) => `${e.ts}-${e.actorId}-${e.type}`
    const cur = get().sessionActivity
    if (cur.some((e) => key(e) === key(event))) return
    set({ sessionActivity: [...cur, event].slice(-500) })
  },
  setSessionActivity: (events) => {
    const key = (e: ActivityEvent) => `${e.ts}-${e.actorId}-${e.type}`
    const map = new Map<string, ActivityEvent>()
    for (const e of [...events, ...get().sessionActivity]) map.set(key(e), e)
    const merged = Array.from(map.values()).sort((a, b) => a.ts - b.ts).slice(-500)
    set({ sessionActivity: merged })
  },
  setSidePanelTab: (tab) => {
    // OSPROD-52: opening the Chat tab IS the "I have read these
    // messages" signal — clear the badge in the same atomic update so
    // the badge never lingers for a tick after the tab swap.
    // OSPROD-73: but ONLY when the panel is actually visible. Selecting
    // the Chat tab while the panel is collapsed shouldn't pretend the
    // user saw the messages — they didn't, the panel is hidden.
    const { isSidePanelCollapsed } = get()
    set(tab === 'chat' && !isSidePanelCollapsed
      ? { sidePanelTab: tab, sidePanelChatUnread: 0 }
      : { sidePanelTab: tab })
    if (typeof window !== 'undefined') {
      try { localStorage.setItem('voiceSidePanelTab', tab) } catch {}
    }
  },
  setSidePanelCollapsed: (collapsed) => {
    // OSPROD-73: expanding while the Chat tab is the active tab means
    // the user is now actually looking at the chat — atomically clear
    // the unread badge in the same update for the same reason
    // setSidePanelTab does.
    const { sidePanelTab } = get()
    set(!collapsed && sidePanelTab === 'chat'
      ? { isSidePanelCollapsed: false, sidePanelChatUnread: 0 }
      : { isSidePanelCollapsed: collapsed })
    if (typeof window !== 'undefined') {
      try { localStorage.setItem('voiceSidePanelCollapsed', String(collapsed)) } catch {}
    }
  },
  toggleSidePanelCollapsed: () => {
    get().setSidePanelCollapsed(!get().isSidePanelCollapsed)
  },
  setSidePanelHeight: (height) => {
    set({ sidePanelHeight: height })
    if (typeof window !== 'undefined') {
      try { localStorage.setItem('voiceSidePanelHeight', String(height)) } catch {}
    }
  },
  setSelfViewHidden: (hidden) => {
    set({ isSelfViewHidden: hidden })
    if (typeof window !== 'undefined') {
      try { localStorage.setItem('voiceSelfViewHidden', String(hidden)) } catch {}
    }
  },
  bumpSidePanelChatUnread: () => {
    // Cap the in-memory count at 100 so the formatter only ever has to
    // render "99+" — anything past that is wasted state and would
    // overflow if a noisy channel kept incrementing for hours.
    const next = Math.min(get().sidePanelChatUnread + 1, 100)
    set({ sidePanelChatUnread: next })
  },
  clearSidePanelChatUnread: () => set({ sidePanelChatUnread: 0 }),
  setChannelViewVisible: (visible) => {
    set({ isChannelViewVisible: visible })
  },

  setRaisedHand: (identity, raised) => {
    const next = new Set(get().raisedHands)
    if (raised) next.add(identity)
    else next.delete(identity)
    set({ raisedHands: next })
  },

  toggleLocalRaisedHand: () => {
    const room = useLivekitStore.getState().room
    if (!room) return
    const me = room.localParticipant.identity
    // The self-tile keys `participant.userId` by the local DB user id
    // (see VoiceChannelView's merge — `userId: currentUser?.id`), while
    // remote tiles key by LiveKit identity. The `raisedHands` set is
    // looked up against `participant.userId`, so the local entry must
    // be stored under the DB id or the user never sees their own raised
    // hand even though the broadcast lands fine on every other client.
    // Add both keys so we're robust to either lookup path.
    const selfTileKey = getCurrentUserId() || me
    const currently = get().raisedHands.has(selfTileKey)
    const nextRaised = !currently
    const next = new Set(get().raisedHands)
    if (nextRaised) {
      next.add(selfTileKey)
      next.add(me)
    } else {
      next.delete(selfTileKey)
      next.delete(me)
    }
    set({ raisedHands: next })
    emitVoiceActivity(get().currentChannelId, nextRaised ? 'hand_raise' : 'hand_lower')
    // Broadcast over LiveKit's data channel — small, ephemeral, no
    // server round-trip. Other clients pick it up via DataReceived in
    // livekitStore.connect(). Reliable=true so a quick on/off doesn't
    // get reordered or dropped. Identity stays the LiveKit one — that's
    // what remotes match against their own remote-tile keying.
    try {
      const payload = new TextEncoder().encode(
        JSON.stringify({ type: 'raise-hand', identity: me, raised: nextRaised })
      )
      room.localParticipant.publishData(payload, { reliable: true })
    } catch (err) {
      console.error('Failed to publish raise-hand:', err)
    }
  },

  setAway: (identity, away) => {
    const next = new Set(get().awayParticipants)
    if (away) next.add(identity)
    else next.delete(identity)
    set({ awayParticipants: next })
  },

  setLocalAway: (away) => {
    // Mirror toggleLocalRaisedHand: tiles look up by participant.userId
    // for the self-row but by LiveKit identity for remotes, so we have
    // to seed both keys to surface the hourglass on our own tile.
    // Idempotent — bail when already in target state so the bidirectional
    // mute ↔ BRB cascade can't ping-pong between the stores.
    const room = useLivekitStore.getState().room
    if (!room) return
    const me = room.localParticipant.identity
    const selfTileKey = getCurrentUserId() || me
    const currently = get().awayParticipants.has(selfTileKey)
    if (currently === away) return
    const next = new Set(get().awayParticipants)
    if (away) {
      next.add(selfTileKey)
      next.add(me)
    } else {
      next.delete(selfTileKey)
      next.delete(me)
    }
    set({ awayParticipants: next })
    emitVoiceActivity(get().currentChannelId, away ? 'away_on' : 'away_off')
    // Broadcast over the LiveKit data channel — same path raise-hand
    // uses. Reliable so a quick on/off doesn't get reordered.
    try {
      const payload = new TextEncoder().encode(
        JSON.stringify({ type: 'away', identity: me, away })
      )
      room.localParticipant.publishData(payload, { reliable: true })
    } catch (err) {
      console.error('Failed to publish away:', err)
    }
    // OSPROD-61: going BRB also turns the camera off. Centralised here
    // so every activation path (BRB button + deafen cascade) gets it
    // without each call site re-implementing the guard. Coming back
    // from BRB leaves the camera off — the user decides when to show
    // their face again.
    if (away) {
      const livekit = useLivekitStore.getState()
      const cameraOn = livekit.optimisticVideoEnabled ?? room.localParticipant.isCameraEnabled
      if (cameraOn) livekit.toggleVideo()
    }
  },

  toggleLocalAway: () => {
    // OSPROD-61: BRB is coupled to DEAFEN (not mute). Plain mic mute
    // never sets BRB; only deafen (or this button) does.
    //   ON  → also deafen (which implies mute server-side + camera off
    //         via setLocalAway). User is fully off-air.
    //   OFF → also undeafen (restores preDeafenMuted). If the deafen
    //         restore leaves the mic still muted, we explicitly unmute
    //         so "I'm back" really means back to talking. Camera stays
    //         off — the user reveals their face on their own terms.
    const room = useLivekitStore.getState().room
    if (!room) return
    const selfTileKey = getCurrentUserId() || room.localParticipant.identity
    const currently = get().awayParticipants.has(selfTileKey)
    const nextAway = !currently
    get().setLocalAway(nextAway)

    const auth = useAuthStore.getState()
    const settings = auth.user?.settings ?? {}
    if (nextAway) {
      if (!settings.isDeafened) auth.toggleDeafen()
    } else {
      if (settings.isDeafened) auth.toggleDeafen()
      // Read fresh — toggleDeafen optimistically updates user.settings
      // synchronously, so we see the post-undeafen mute state here.
      const after = useAuthStore.getState().user?.settings ?? {}
      if (after.isMuted) auth.toggleMute()
    }
  },

  pushReaction: (identity, emoji) => {
    if (!identity || !emoji) return
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
    const burst: LiveReaction = { id, emoji, ts: Date.now() }
    const current = get().participantReactions[identity] || []
    set({
      participantReactions: {
        ...get().participantReactions,
        [identity]: [...current, burst],
      },
    })
    // Reap after the animation completes. Looking up the array fresh
    // (not via closure) so concurrent bursts on the same tile don't
    // clobber each other when one expires.
    setTimeout(() => {
      const map = get().participantReactions
      const list = map[identity]
      if (!list) return
      const next = list.filter((r) => r.id !== id)
      const nextMap = { ...map }
      if (next.length === 0) delete nextMap[identity]
      else nextMap[identity] = next
      set({ participantReactions: nextMap })
    }, REACTION_LIFETIME_MS + 100)
  },

  sendReaction: (emoji) => {
    const room = useLivekitStore.getState().room
    if (!room) return false
    const me = room.localParticipant.identity
    if (!me || !emoji) return false
    const now = Date.now()
    if (now - lastReactionSentAt < REACTION_SEND_COOLDOWN_MS) return false
    lastReactionSentAt = now
    emitVoiceActivity(get().currentChannelId, 'reaction', { emoji })
    // Local echo. The self-tile in VoiceChannelView keys participants
    // by `currentUser.id` (DB id) while remotes key by LiveKit identity
    // (username). Pushing under the local DB id ensures the sender
    // sees their own emoji float on their own tile.
    const selfDbId = getCurrentUserId() || me
    get().pushReaction(selfDbId, emoji)
    try {
      const payload = new TextEncoder().encode(
        JSON.stringify({ type: 'reaction', identity: me, emoji })
      )
      room.localParticipant.publishData(payload, { reliable: true })
    } catch (err) {
      console.error('Failed to publish reaction:', err)
    }
    return true
  },
}))
